import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Tooltip,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { format } from 'date-fns';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  filterControl: {
    minWidth: 200,
  },
  table: {
    minWidth: 650,
  },
  expandedRow: {
    backgroundColor: '#f5f5f5',
  },
  expandedContent: {
    padding: theme.spacing(2),
  },
  status: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  iframeContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    overflow: 'hidden',
    minHeight: 400,
  },
});

const EmailLogViewer = ({ classes, logs, templates, sequences, triggers = [], onFilterChange, onExportCSV }) => {
  const [userFilter, setUserFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [sequenceFilter, setSequenceFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  const handleFilterChange = () => {
    if (onFilterChange) {
      onFilterChange({
        userFilter,
        templateFilter: templateFilter === 'all' ? null : templateFilter,
        sequenceFilter: sequenceFilter === 'all' ? null : sequenceFilter,
        triggerFilter: triggerFilter === 'all' ? null : triggerFilter,
        searchTerm,
      });
    }
  };

  const filteredLogs = logs.filter(log => {
    const matchesUser = !userFilter ||
                       (log.user?.name && log.user.name.toLowerCase().includes(userFilter.toLowerCase())) ||
                       (log.user?.email && log.user.email.toLowerCase().includes(userFilter.toLowerCase())) ||
                       (log.recipientEmail && log.recipientEmail.toLowerCase().includes(userFilter.toLowerCase()));
    const matchesTemplate = templateFilter === 'all' || log.emailTemplate?.id === templateFilter;
    const matchesSequence = sequenceFilter === 'all' || log.emailSequence?.id === sequenceFilter;
    const matchesTrigger = triggerFilter === 'all' || log.emailTrigger?.id === triggerFilter;
    const matchesSearch = !searchTerm || 
                         (log.subject && log.subject.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (log.user?.name && log.user.name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesUser && matchesTemplate && matchesSequence && matchesTrigger && matchesSearch;
  });

  const handleExport = () => {
    const csvData = filteredLogs.map(log => ({
      'Sent Date': formatDate(log.sentAt),
      'Recipient': log.user?.name || log.recipientEmail || '',
      'Recipient Email': log.user?.email || log.recipientEmail || '',
      'Sent By': log.sentBy?.name || 'System',
      'Template': log.emailTemplate?.name || '',
      'Sequence': log.emailSequence?.name || '',
      'Trigger': log.emailTrigger?.name || '',
      'Subject': log.subject || '',
      'Opened': log.openedAt ? 'Yes' : 'No',
      'Clicked': log.clickedAt ? 'Yes' : 'No',
    }));

    // Convert to CSV format
    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => headers.map(header => `"${row[header] || ''}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `email_audit_log_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    if (onExportCSV) {
      onExportCSV(csvData);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (e) {
      return dateString;
    }
  };

  const handleRowExpand = (logId) => {
    setExpandedRow(expandedRow === logId ? null : logId);
  };

  const handlePreview = (html) => {
    setPreviewHtml(html);
    setPreviewOpen(true);
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">Email Audit Log</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<GetAppIcon />}
          onClick={handleExport}
          disabled={filteredLogs.length === 0}
        >
          Export to CSV
        </Button>
      </div>

      <div className={classes.filters}>
        <TextField
          label="Search by user or subject"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.filterControl}
        />
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Template</InputLabel>
          <Select
            value={templateFilter}
            onChange={(e) => setTemplateFilter(e.target.value)}
            label="Template"
          >
            <MenuItem value="all">All Templates</MenuItem>
            {templates.map(template => (
              <MenuItem key={template.id} value={template.id}>
                {template.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Sequence</InputLabel>
          <Select
            value={sequenceFilter}
            onChange={(e) => setSequenceFilter(e.target.value)}
            label="Sequence"
          >
            <MenuItem value="all">All Sequences</MenuItem>
            {sequences.map(sequence => (
              <MenuItem key={sequence.id} value={sequence.id}>
                {sequence.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Trigger</InputLabel>
          <Select
            value={triggerFilter}
            onChange={(e) => setTriggerFilter(e.target.value)}
            label="Trigger"
          >
            <MenuItem value="all">All Triggers</MenuItem>
            {triggers.map(trigger => (
              <MenuItem key={trigger.id} value={trigger.id}>
                {trigger.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell width="50"></TableCell>
              <TableCell>Sent Date</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Sent By</TableCell>
              <TableCell>Template</TableCell>
              <TableCell>Sequence</TableCell>
              <TableCell>Trigger</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredLogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {logs.length === 0
                      ? 'No emails have been sent yet.'
                      : 'No emails match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {filteredLogs.map(log => (
                  <React.Fragment key={log.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleRowExpand(log.id)}
                        >
                          {expandedRow === log.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>{formatDate(log.sentAt)}</TableCell>
                      <TableCell>
                        <div>
                          {/* Transactional sends to off-platform agents have no
                              linked user; recipientEmail still identifies them. */}
                          <Typography variant="body2">
                            {log.user?.name || log.recipientEmail || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {log.user?.email || log.recipientEmail || ''}
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.sentBy?.name || 'System'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.emailTemplate?.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {log.emailSequence?.name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {log.emailTrigger ? (
                          <Chip
                            label={log.emailTrigger.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Typography variant="body2">-</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{log.subject}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <div className={classes.status}>
                          {log.openedAt && (
                            <Tooltip title={`Opened: ${formatDate(log.openedAt)}`}>
                              <VisibilityIcon fontSize="small" color="primary" />
                            </Tooltip>
                          )}
                          {log.clickedAt && (
                            <Tooltip title={`Clicked: ${formatDate(log.clickedAt)}`}>
                              <CheckCircleIcon fontSize="small" color="secondary" />
                            </Tooltip>
                          )}
                          {!log.openedAt && !log.clickedAt && (
                            <Typography variant="caption" color="textSecondary">
                              Sent
                            </Typography>
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          onClick={() => handlePreview(log.bodyHtml)}
                          variant="outlined"
                        >
                          Preview
                        </Button>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                        <Collapse in={expandedRow === log.id} timeout="auto" unmountOnExit>
                          <Box className={classes.expandedContent}>
                            <Typography variant="subtitle2" gutterBottom>
                              Additional Details
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">
                                  Template Version:
                                </Typography>
                                <Typography variant="body2">
                                  {log.templateVersion || 'N/A'}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="textSecondary">
                                  Sent At:
                                </Typography>
                                <Typography variant="body2">
                                  {formatDate(log.sentAt)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Email Preview</DialogTitle>
        <DialogContent>
          <Box className={classes.iframeContainer}>
            <iframe
              srcDoc={previewHtml}
              style={{ width: '100%', height: 400, border: 'none' }}
              title="Email Preview"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

EmailLogViewer.propTypes = {
  classes: PropTypes.object.isRequired,
  logs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      sentAt: PropTypes.string,
      openedAt: PropTypes.string,
      clickedAt: PropTypes.string,
      subject: PropTypes.string,
      bodyHtml: PropTypes.string,
      templateVersion: PropTypes.number,
      user: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        email: PropTypes.string,
      }),
      sentBy: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      emailTemplate: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      emailSequence: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      emailTrigger: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        eventType: PropTypes.string,
      }),
    })
  ).isRequired,
  templates: PropTypes.array,
  sequences: PropTypes.array,
  triggers: PropTypes.array,
  onFilterChange: PropTypes.func,
  onExportCSV: PropTypes.func,
};

EmailLogViewer.defaultProps = {
  templates: [],
  sequences: [],
  triggers: [],
};

export default withStyles(styles)(EmailLogViewer);

